import {
  createContext,
  useContext,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { ChildSearchTab } from "@/components/records/child/ChildSearchTabs";

type AddChildContextType = {
  openAddChildWizard: () => void;
  registerOpenHandler: (handler: () => void) => void;
  openChildTab: (tab: ChildSearchTab) => void;
  registerTabHandler: (handler: (tab: ChildSearchTab) => void) => void;
};

const AddChildContext = createContext<AddChildContextType | undefined>(
  undefined
);

export function AddChildProvider({ children }: { children: ReactNode }) {
  const openHandlerRef = useRef<(() => void) | null>(null);
  const tabHandlerRef = useRef<((tab: ChildSearchTab) => void) | null>(null);

  const openAddChildWizard = useCallback(() => {
    if (openHandlerRef.current) {
      openHandlerRef.current();
    }
  }, []);

  const registerOpenHandler = useCallback((handler: () => void) => {
    openHandlerRef.current = handler;
  }, []);

  const openChildTab = useCallback((tab: ChildSearchTab) => {
    if (tabHandlerRef.current) {
      tabHandlerRef.current(tab);
    }
  }, []);

  const registerTabHandler = useCallback(
    (handler: (tab: ChildSearchTab) => void) => {
      tabHandlerRef.current = handler;
    },
    []
  );

  return (
    <AddChildContext.Provider
      value={{
        openAddChildWizard,
        registerOpenHandler,
        openChildTab,
        registerTabHandler,
      }}
    >
      {children}
    </AddChildContext.Provider>
  );
}

export function useAddChild() {
  const context = useContext(AddChildContext);
  if (!context) {
    throw new Error("useAddChild must be used within AddChildProvider");
  }
  return context;
}
